import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCoaching } from '../../contexts/CoachingContext';
import toast from 'react-hot-toast';

const AXES = ['Marché', 'Production', 'Ressources Humaines', 'Financement', 'Comptabilité', 'Rentabilité', 'Recherche & Développement'];

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const IconTrash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const emptyAxe = {
  axis: AXES[0],
  actions_implemented: '',
  balance_sheet: '',
  topics_discussed: '',
  actions_to_implement: '',
  implementation_period: '',
  responsible: ''
};

const AxeForm = ({ value, onChange, onRemove, index }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 relative">
    <div className="flex items-center justify-between mb-1">
      <div className="flex items-center space-x-2">
        <span className="text-xs font-semibold text-gray-500">AXON {index + 1}</span>
        <select
          value={value.axis}
          onChange={e => onChange({ ...value, axis: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          {AXES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <button onClick={onRemove} className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"><IconTrash /></button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Actions mises en oeuvre (période précédente)</label>
        <textarea rows={3} value={value.actions_implemented} onChange={e => onChange({ ...value, actions_implemented: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Décrire les actions déjà entreprises..." />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Bilan des actions prévues/réalisées</label>
        <textarea rows={3} value={value.balance_sheet} onChange={e => onChange({ ...value, balance_sheet: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Résultats obtenus, écarts..." />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Sujets abordés lors du coaching</label>
        <textarea rows={3} value={value.topics_discussed} onChange={e => onChange({ ...value, topics_discussed: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Thèmes discutés, problèmes soulevés..." />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Actions à mettre en oeuvre</label>
        <textarea rows={3} value={value.actions_to_implement} onChange={e => onChange({ ...value, actions_to_implement: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Prochaines étapes décidées..." />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Période de mise en oeuvre</label>
        <input type="text" value={value.implementation_period} onChange={e => onChange({ ...value, implementation_period: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : Janvier–Mars 2024" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Responsable</label>
        <input type="text" value={value.responsible} onChange={e => onChange({ ...value, responsible: e.target.value })} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Nom du responsable..." />
      </div>
    </div>
  </div>
);

const SessionCard = ({ session, onSelect, selected }) => (
  <div
    onClick={() => onSelect(session)}
    className={`p-4 rounded-lg border cursor-pointer transition-all ${
      selected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300 bg-white'
    }`}
  >
    <div className="flex items-center justify-between mb-1">
      <span className="font-medium text-sm text-gray-900">
        {session.coaching_entrepreneurs?.company_name || 'N/A'}
      </span>
      <span className={`text-xs px-2 py-0.5 rounded-full ${
        session.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
      }`}>
        {session.status === 'validated' ? 'Validé' : 'Brouillon'}
      </span>
    </div>
    <p className="text-xs text-gray-500">
      {new Date(session.session_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
    </p>
    {session.next_session_date && (
      <p className="text-xs text-gray-400 mt-1">Prochain : {new Date(session.next_session_date).toLocaleDateString('fr-FR')}</p>
    )}
  </div>
);

const CoachingMinutes = () => {
  const [searchParams] = useSearchParams();
  const entrepreneurIdParam = searchParams.get('entrepreneur');

  const { entrepreneurs, sessions, loading, fetchEntrepreneurs, fetchSessions, fetchSession, createSession, updateSession, deleteSession } = useCoaching();

  const [filterEntrepreneur, setFilterEntrepreneur] = useState(entrepreneurIdParam || '');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [mode, setMode] = useState('list'); // 'list' | 'view' | 'new' | 'edit'
  const [savingStatus, setSavingStatus] = useState(null);

  // Form state for new/edit
  const [form, setForm] = useState({
    entrepreneur_id: '',
    session_date: '',
    next_session_date: '',
    periodicity: '',
    general_notes: ''
  });
  const [axes, setAxes] = useState([{ ...emptyAxe }]);

  useEffect(() => {
    fetchEntrepreneurs();
    fetchSessions(entrepreneurIdParam || undefined);
  }, [fetchEntrepreneurs, fetchSessions, entrepreneurIdParam]);

  const loadSessionDetail = useCallback(async (session) => {
    try {
      const detail = await fetchSession(session.id);
      setSessionDetail(detail);
      setSelectedSession(session);
      setMode('view');
    } catch (err) {
      toast.error('Erreur chargement séance');
    }
  }, [fetchSession]);

  const filteredSessions = filterEntrepreneur
    ? sessions.filter(s => s.entrepreneur_id === filterEntrepreneur)
    : sessions;

  const startNew = () => {
    setForm({ entrepreneur_id: filterEntrepreneur || '', session_date: '', next_session_date: '', periodicity: '', general_notes: '' });
    setAxes([{ ...emptyAxe }]);
    setSelectedSession(null);
    setSessionDetail(null);
    setMode('new');
  };

  const startEdit = () => {
    if (!sessionDetail) return;
    setForm({
      entrepreneur_id: sessionDetail.entrepreneur_id,
      session_date: sessionDetail.session_date,
      next_session_date: sessionDetail.next_session_date || '',
      periodicity: sessionDetail.periodicity || '',
      general_notes: sessionDetail.general_notes || ''
    });
    setAxes(sessionDetail.axes && sessionDetail.axes.length > 0
      ? sessionDetail.axes.map(a => ({
          id: a.id,
          axis: a.axis,
          actions_implemented: a.actions_implemented || '',
          balance_sheet: a.balance_sheet || '',
          topics_discussed: a.topics_discussed || '',
          actions_to_implement: a.actions_to_implement || '',
          implementation_period: a.implementation_period || '',
          responsible: a.responsible || ''
        }))
      : [{ ...emptyAxe }]
    );
    setMode('edit');
  };

  const handleSave = async () => {
    if (!form.entrepreneur_id || !form.session_date) {
      toast.error('Entrepreneur et date de séance requis');
      return;
    }
    setSavingStatus('saving');
    try {
      if (mode === 'new') {
        const session = await createSession({ ...form, axes: axes.filter(a => a.axis) });
        await loadSessionDetail(session);
      } else if (mode === 'edit' && sessionDetail) {
        await updateSession(sessionDetail.id, form);
        const detail = await fetchSession(sessionDetail.id);
        setSessionDetail(detail);
        setMode('view');
      }
      setSavingStatus('saved');
      setTimeout(() => setSavingStatus(null), 2000);
    } catch (err) {
      setSavingStatus(null);
    }
  };

  const handleDelete = async () => {
    if (!sessionDetail) return;
    if (!window.confirm('Supprimer ce procès verbal ? Cette action est irréversible.')) return;
    await deleteSession(sessionDetail.id);
    setSelectedSession(null);
    setSessionDetail(null);
    setMode('list');
    await fetchSessions(filterEntrepreneur || undefined);
  };

  const handleValidate = async () => {
    if (!sessionDetail) return;
    await updateSession(sessionDetail.id, { status: 'validated' });
    setSessionDetail(prev => ({ ...prev, status: 'validated' }));
    toast.success('Séance validée');
  };

  const downloadCSV = () => {
    if (!sessionDetail) return;
    const ent = entrepreneurs.find(e => e.id === sessionDetail.entrepreneur_id);
    const headers = ['Entreprise', 'Date séance', 'Prochain coaching', 'Axe', 'Actions mises en oeuvre', 'Bilan', 'Sujets abordés', 'Actions à mettre en oeuvre', 'Période', 'Responsable'];
    const rows = (sessionDetail.axes || []).map(ax => [
      ent?.company_name || '',
      new Date(sessionDetail.session_date).toLocaleDateString('fr-FR'),
      sessionDetail.next_session_date ? new Date(sessionDetail.next_session_date).toLocaleDateString('fr-FR') : '',
      ax.axis, ax.actions_implemented, ax.balance_sheet, ax.topics_discussed,
      ax.actions_to_implement, ax.implementation_period, ax.responsible
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pv-coaching-${sessionDetail.session_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Procès Verbal de coaching</h1>
          <p className="text-sm text-gray-500 mt-0.5">Enregistrez chaque séance de coaching par entrepreneur</p>
        </div>
        <button
          onClick={startNew}
          className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
        >
          <IconPlus /><span>Nouvelle séance</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Panneau gauche — liste séances */}
        <div className="space-y-3">
          <div>
            <select
              value={filterEntrepreneur}
              onChange={e => { setFilterEntrepreneur(e.target.value); fetchSessions(e.target.value || undefined); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value="">Tous les entrepreneurs</option>
              {entrepreneurs.map(e => <option key={e.id} value={e.id}>{e.company_name}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg" />)}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-400">Aucune séance enregistrée</p>
              <button onClick={startNew} className="mt-2 text-primary text-sm font-medium hover:underline">Créer la première séance</button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSessions.map(s => (
                <SessionCard
                  key={s.id}
                  session={s}
                  onSelect={loadSessionDetail}
                  selected={selectedSession?.id === s.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Panneau droit — détail / formulaire */}
        <div className="lg:col-span-2">
          {mode === 'list' && !selectedSession && (
            <div className="bg-white border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-400">
              <p>Sélectionnez une séance ou créez-en une nouvelle</p>
            </div>
          )}

          {/* Formulaire création / édition */}
          {(mode === 'new' || mode === 'edit') && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">{mode === 'new' ? 'Nouvelle séance de coaching' : 'Modifier la séance'}</h2>
                <div className="flex space-x-2">
                  <button onClick={() => setMode(sessionDetail ? 'view' : 'list')} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50">Annuler</button>
                  <button
                    onClick={handleSave}
                    disabled={savingStatus === 'saving'}
                    className="px-4 py-1.5 text-sm bg-primary text-white rounded font-medium hover:bg-primary-600 disabled:opacity-60"
                  >
                    {savingStatus === 'saving' ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {/* Informations générales */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entrepreneur *</label>
                    <select
                      value={form.entrepreneur_id}
                      onChange={e => setForm(p => ({ ...p, entrepreneur_id: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={mode === 'edit'}
                    >
                      <option value="">-- Sélectionner --</option>
                      {entrepreneurs.map(e => <option key={e.id} value={e.id}>{e.company_name} — {e.entrepreneur_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de la séance *</label>
                    <input
                      type="date"
                      value={form.session_date}
                      onChange={e => setForm(p => ({ ...p, session_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date du prochain coaching</label>
                    <input
                      type="date"
                      value={form.next_session_date}
                      onChange={e => setForm(p => ({ ...p, next_session_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Périodicité des séances</label>
                    <select
                      value={form.periodicity}
                      onChange={e => setForm(p => ({ ...p, periodicity: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    >
                      <option value="">À définir</option>
                      <option value="Hebdomadaire">Hebdomadaire</option>
                      <option value="Bi-mensuelle">Bi-mensuelle</option>
                      <option value="Mensuelle">Mensuelle</option>
                      <option value="Bimestrielle">Bimestrielle</option>
                      <option value="Trimestrielle">Trimestrielle</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes générales</label>
                  <textarea
                    rows={3}
                    value={form.general_notes}
                    onChange={e => setForm(p => ({ ...p, general_notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Observations générales de la séance..."
                  />
                </div>

                {/* Axes — seulement pour nouvelle séance */}
                {mode === 'new' && (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-800">Axes abordés</h3>
                      <button
                        onClick={() => setAxes(p => [...p, { ...emptyAxe }])}
                        className="flex items-center space-x-1 text-sm text-primary font-medium hover:underline"
                      >
                        <IconPlus /><span>Ajouter un axe</span>
                      </button>
                    </div>
                    {axes.map((axe, i) => (
                      <AxeForm
                        key={i}
                        index={i}
                        value={axe}
                        onChange={val => setAxes(p => p.map((a, idx) => idx === i ? val : a))}
                        onRemove={() => setAxes(p => p.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vue détail séance */}
          {mode === 'view' && sessionDetail && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">
                      {sessionDetail.coaching_entrepreneurs?.company_name} — {new Date(sessionDetail.session_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </h2>
                    {sessionDetail.periodicity && <p className="text-xs text-gray-500 mt-0.5">Périodicité : {sessionDetail.periodicity}</p>}
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={downloadCSV} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
                      <IconDownload /><span>CSV</span>
                    </button>
                    {sessionDetail.status !== 'validated' && (
                      <button onClick={handleValidate} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-green-300 rounded text-green-700 hover:bg-green-50">
                        <IconCheck /><span>Valider</span>
                      </button>
                    )}
                    <button onClick={startEdit} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50">
                      <IconEdit /><span>Modifier</span>
                    </button>
                    <button onClick={handleDelete} className="flex items-center space-x-1 px-3 py-1.5 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50">
                      <IconTrash /><span>Supprimer</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Infos générales */}
                <div className="flex flex-wrap gap-4 text-sm pb-4 border-b border-gray-100">
                  <div><span className="text-gray-500">Entrepreneur : </span><span className="font-medium">{sessionDetail.coaching_entrepreneurs?.entrepreneur_name || 'N/A'}</span></div>
                  {sessionDetail.next_session_date && (
                    <div><span className="text-gray-500">Prochain coaching : </span><span className="font-medium">{new Date(sessionDetail.next_session_date).toLocaleDateString('fr-FR')}</span></div>
                  )}
                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sessionDetail.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {sessionDetail.status === 'validated' ? 'Validé' : 'Brouillon'}
                    </span>
                  </div>
                </div>

                {sessionDetail.general_notes && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">Notes générales</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{sessionDetail.general_notes}</p>
                  </div>
                )}

                {/* Axes du PV */}
                {sessionDetail.axes && sessionDetail.axes.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800">Détail par axe</h3>
                    {sessionDetail.axes.map((axe, i) => (
                      <div key={axe.id || i} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                          <span className="font-semibold text-gray-800 text-sm">{axe.axis}</span>
                        </div>
                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {axe.actions_implemented && (
                            <div>
                              <p className="font-medium text-gray-600 text-xs uppercase tracking-wide mb-1">Actions mises en oeuvre</p>
                              <p className="text-gray-800">{axe.actions_implemented}</p>
                            </div>
                          )}
                          {axe.balance_sheet && (
                            <div>
                              <p className="font-medium text-gray-600 text-xs uppercase tracking-wide mb-1">Bilan</p>
                              <p className="text-gray-800">{axe.balance_sheet}</p>
                            </div>
                          )}
                          {axe.topics_discussed && (
                            <div>
                              <p className="font-medium text-gray-600 text-xs uppercase tracking-wide mb-1">Sujets abordés</p>
                              <p className="text-gray-800">{axe.topics_discussed}</p>
                            </div>
                          )}
                          {axe.actions_to_implement && (
                            <div>
                              <p className="font-medium text-gray-600 text-xs uppercase tracking-wide mb-1">Actions à mettre en oeuvre</p>
                              <p className="text-gray-800">{axe.actions_to_implement}</p>
                            </div>
                          )}
                          {(axe.implementation_period || axe.responsible) && (
                            <div className="md:col-span-2 flex gap-6">
                              {axe.implementation_period && (
                                <div><span className="text-gray-500 text-xs">Période : </span><span className="font-medium">{axe.implementation_period}</span></div>
                              )}
                              {axe.responsible && (
                                <div><span className="text-gray-500 text-xs">Responsable : </span><span className="font-medium">{axe.responsible}</span></div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Aucun axe défini pour cette séance.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachingMinutes;
